import type { Variant } from '~/types/models/commercetools/Variant.model';
import type { ProductModels } from '~/types/models/product.models.ts';
import { ProductAttributes, ProductKeys } from '~/types/ProductEnums';
import { countries as expressDeliveryCompatibleCountries } from '~/static/data/express-countries.json';

/**
 * Determines if the specified product is available in the given country.
 *
 * @param      {ProductModels.TransformedProductModel}  product                The product
 * @param      {}                                       ctContext              The ct context
 * @param      {string}                                 [countryToCheck=null]  The country to check
 * @return     {boolean}                                True if product available in country, False otherwise.
 */
export function isProductAvailableInCountry(
  product: ProductModels.TransformedProductModel,
  countryToCheck: string = null,
): boolean {
  return !!product.attributesRaw
    .find((attribute) => attribute?.name === ProductAttributes.availableInCountries)
    ?.value?.find((country) => country?.key === countryToCheck || country?.key === 'ALL');
}

/**
 * Determines whether the specified product is product in stock.
 *
 * @param      {(ProductModels.TransformedProductModel|Variant)}  product  The product
 * @return     {boolean}                                          True if the specified product is product in stock, False otherwise.
 */
export function isProductInStock(product: ProductModels.TransformedProductModel | Variant): boolean {
  const isSellableAttribute = product.attributesRaw?.find((attribute) => attribute.name === ProductKeys.isSellable);

  return isSellableAttribute?.value?.key === ProductKeys.inStock;
}

/**
 * Determines whether the specified product is out of stock.
 *
 * @param      {ProductModels.TransformedProductModel}  product  The product
 * @return     {boolean}                                True if the specified product is product out of stock, False otherwise.
 */
export function isProductOutOfStock(product: ProductModels.TransformedProductModel): boolean {
  const isSellableAttribute = product.attributesRaw?.find((attribute) => attribute.name === ProductKeys.isSellable);

  return isSellableAttribute?.value?.key === ProductKeys.outOfStock;
}

/**
 * Determines whether the specified product is low on stock.
 *
 * @param      {ProductModels.TransformedProductModel}  product  The product
 * @return     {boolean}                                True if the specified product is product low stock, False otherwise.
 */
export function isProductLowStock(product: ProductModels.TransformedProductModel): boolean {
  const isLowStockAttribute = product.attributesRaw?.find((attribute) => attribute.name === ProductKeys.lowStock);

  return isLowStockAttribute?.value === true;
}

/**
 * Determines whether the specified product is discontinued.
 *
 * @param      {ProductModels.TransformedProductModel}  product  The product
 * @return     {boolean}                                True if the specified product is product discontinued, False otherwise.
 */
export function isProductDiscontinued(product: ProductModels.TransformedProductModel): boolean {
  const isProductDiscontinuedAttributeValue = product.attributesRaw?.find(
    (attribute) => attribute.name === ProductKeys.isProductDiscontinued,
  )?.value; // true/false
  const isSellableAttributeValue = product.attributesRaw?.find(
    (attribute) => attribute.name === ProductKeys.isSellable,
  )?.value; // key = "Discontinued|InStock|OutOfStock"

  return isProductDiscontinuedAttributeValue || isSellableAttributeValue?.key === ProductKeys.discontinued;
}

/**
 * Determines if the given product can be delivered expressly in the given
 * country.
 *
 * @param      {Object}   product         The product to deliver (it needs to
 *                                        have an array `attributesRaw` inside)
 * @param      {String}   currentCountry  The current country
 * @return     {boolean}  True if the product can be delivered expressly, False
 *                        otherwise.
 */
export function isProductExpressDeliveryCompatible(
  product: ProductModels.TransformedProductModel,
  currentCountry: string,
): boolean {
  if (
    expressDeliveryCompatibleCountries.includes(currentCountry) &&
    isProductInStock(product) &&
    isProductLowStock(product)
  ) {
    return false;
  }
  return true;
}

/**
 * Determines whether the specified product has all its variants out of stock.
 *
 * @param      {ProductModels.TransformedProductModel}  product  The product
 * @return     {boolean}                                True if the specified product has all variants oos, False otherwise.
 */
export function isAllProductVariantsOOS(product: ProductModels.TransformedProductModel): boolean {
  const allProductVariants = [product, ...(product.variants || [])];

  return allProductVariants.every((variant) => !isProductInStock(variant));
}

/**
 * Determines whether the specified product has any variant sellable.
 *
 * @param      {ProductModels.TransformedProductModel}  product  The product
 * @return     {boolean}                                True if the specified product has any variant sellable, False otherwise.
 */
export function isAnyVariantSellable(product: ProductModels.TransformedProductModel): boolean {
  const allProductVariants = [product, ...(product.variants || [])];

  return allProductVariants.some((variant) => isProductInStock(variant));
}
